import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fetchHistoricalPrices } from "../api/artistQuoteService";

const localeFor = (language) => {
  if (language?.startsWith("de")) return "de-DE";
  if (language?.startsWith("en")) return "en-GB";
  return "fr-FR";
};

export default function QuoteChart({ artistName, medium }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState([]);
  const locale = localeFor(i18n.resolvedLanguage || i18n.language);

  useEffect(() => {
    async function loadPrices() {
      if (!artistName || !medium) return;
      const result = await fetchHistoricalPrices(artistName, medium);
      setData(result || []);
    }
    loadPrices();
  }, [artistName, medium]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);

  const formatMonth = (value) => {
    if (!value) return value;
    const source = /^\d{4}-\d{2}$/.test(String(value)) ? `${value}-01` : value;
    const date = new Date(source);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" }).format(date);
  };

  if (!data.length) {
    return <p className="text-sm text-red-500">{t("artistQuoteTools.chart.noData")}</p>;
  }

  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold mb-4">📈 {t("artistQuoteTools.chart.title")}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" tick={{ fontSize: 12 }} tickFormatter={formatMonth} />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => formatCurrency(value)} />
          <Tooltip
            formatter={(value) => [formatCurrency(value), t("artistQuoteTools.chart.averageValue")]}
            labelFormatter={formatMonth}
          />
          <Line type="monotone" dataKey="average_value" stroke="#4f46e5" strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
